import classNames from 'classnames';
import { Button } from 'components/1-atoms/Buttons';
import { useState } from 'react';
import styles from './NewsLetterSignUp.module.scss';
import { NewsLetterSignUpCheckBox, NewsLetterSignUpSelect, NewsLetterSignUpTextBox } from './NewsLetterSignUpComponents';

export type CreateSubscriberProps = {
	organisationName: string;
	inputs: { [key: string]: any };
};

type fieldTypeTypes = 'TextBox' | 'CheckBox' | 'DropDown';
type optionListType = Array<{ name: string; idValue: string; idName: string }>;
export interface NewsLetterSignUpProps {
	formFields?: Array<{ label: string; idName: string; fieldType: fieldTypeTypes; required?: 'true' | 'false'; optionList?: optionListType }>;
	organisationName: string;
	submitText: string;
	noNewslettersSelectedError: string;
	apiErrorText: string;
	successText: string;
	signUpHandler: (params: CreateSubscriberProps) => Promise<Record<'data', unknown>>;
}

export const NewsLetterSignUp: React.FC<NewsLetterSignUpProps> = ({
	formFields,
	organisationName,
	submitText,
	noNewslettersSelectedError,
	apiErrorText,
	successText,
	signUpHandler,
}) => {
	const [submitTried, setSubmitTried] = useState(false);
	const [userInput, setUserinput] = useState<CreateSubscriberProps>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [apiError, setApiError] = useState(false);
	const [formDone, setFormDone] = useState(false);

	const handleSubmit = (form: HTMLFormElement) => {
		setApiError(false);
		setSubmitTried(true);
		parseUserInput(form);

		if (!form.checkValidity()) {
			return;
		}

		setIsSubmitting(true);
		signUpHandler(userInput)
			.then((res) => {
				const success = Object.keys(res.data).reduce((prev, current) => prev && res.data[current], true);
				setApiError(!success);
				setFormDone(success);
			})
			.catch(() => {
				setApiError(true);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const parseUserInput = (form: HTMLFormElement) => {
		const data = new FormData(form);

		const inputs = Object.fromEntries(
			formFields.map((field) => {
				if (field.fieldType === 'CheckBox') {
					return [field.idName, data.getAll(field.idName).map((id) => Number(id))];
				}
				return [field.idName, data.get(field.idName) as string];
			}),
		);

		const userInput = {
			organisationName: organisationName,
			inputs: { ...inputs },
		};
		setUserinput(userInput);
	};

	return formDone ? (
		<p>{successText}</p>
	) : (
		<form
			noValidate
			onSubmit={(e) => {
				e.preventDefault();
				handleSubmit(e.target as HTMLFormElement);
			}}
			className={classNames(styles.NewsLetterSignUp)}
		>
			<input type="hidden" value={organisationName} name="organisationName" />
			{formFields?.map((f) => {
				if (f.fieldType === 'TextBox') {
					return (
						<NewsLetterSignUpTextBox
							key={f.idName}
							idName={f.idName}
							lblName={f.label}
							required={f?.required === 'true'}
							submitTried={submitTried}
							onChange={(form) => parseUserInput(form)}
						/>
					);
				}
				if (f.fieldType === 'DropDown') {
					return (
						<NewsLetterSignUpSelect
							key={f.idName}
							idName={f.idName}
							lblName={f.label}
							required={f?.required === 'true'}
							submitTried={submitTried}
							onChange={(form) => parseUserInput(form)}
							options={f.optionList}
						/>
					);
				}
				if (f.fieldType === 'CheckBox') {
					return (
						<NewsLetterSignUpCheckBox
							key={f.idName}
							required={f.required === 'true'}
							idName={f.idName}
							lblName={f.label}
							options={f.optionList}
							onChange={(form) => parseUserInput(form)}
							submitTried={submitTried}
							errorText={noNewslettersSelectedError}
						/>
					);
				}
			})}
			{apiError && <p className={classNames(styles.NewsLetterSignUp___error)}>{apiErrorText}</p>}
			<p>
				<Button type="submit" disabled={isSubmitting}>
					{submitText}
				</Button>
			</p>
		</form>
	);
};
